import React, { Component } from 'react';
import { array, number, func, string, object, bool } from 'prop-types';
import Dropdown from 'ps-components/Dropdown/Dropdown';
import Select from 'ps-components/ReactSelect/ReactSelect';
import AdvancedTable, { formatSubHeader } from 'ps-components/AdvancedTable/AdvancedTable';
import AdvancedCell from 'ps-components/AdvancedTable/AdvancedCell';
import Pagination from 'ps-components/Pagination/Pagination';
import './SMAEmployeeTable.scss';
import { equalsZeroUndefined } from '../helper';
import { applyRounding, parseNumCheckNull } from 'ps-components/lib/formatters';

const ASCENDING = 'asc',
  DESCENDING = 'desc',
  PROPOSED = 'proposed',
  CURRENT = 'current';

const columnHeaders = [
  { accessor: 'full_name', Header: 'Employee', width: 130.5 },
  { accessor: 'org_job_title', Header: 'Job Title', width: 174, rightAlign: true },
  { accessor: 'basis', Header: 'Base Salary', width: 104.4, rightAlign: true },
  { accessor: 'comp_ratio', Header: 'Compa Ratio', width: 104.4, rightAlign: true },
  { accessor: 'range_pen', Header: 'Range Penetration', width: 130.5, rightAlign: true },
  { accessor: 'min_cost', Header: 'Cost to Min', width: 100, rightAlign: true },
  { accessor: 'above_max', Header: 'Above Max', width: 100, rightAlign: true }
];

export default class SMAEmployeeTable extends Component {
  static propTypes = {
    viewRef: object,
    employeeData: array,
    modelId: string,
    grade: string,
    employeeCount: number,
    getEmployees: func,
    gradeTotals: object,
    employeeLoading: bool,
    employeeError: bool,
    saveState: func,
    initState: object,
    basis: string,
    basisOptions: array
  };

  sortOnDropdownItems = ['Proposed', 'Current'];

  state = {
    limit: 50,
    offset: 0,
    sortColumn: { columnName: 'full_name', order: ASCENDING },
    sortOn: 'proposed',
    show: 'all',
    subShow: CURRENT
  };

  constructor(props) {
    super(props);
    this.scrollTarget = React.createRef();
    this.getRowElement = this.getRowElement.bind(this);
    this.getRoundedPercent = this.getRoundedPercent.bind(this);
    this.refreshEmployees = this.refreshEmployees.bind(this);
    this.onSortChange = this.onSortChange.bind(this);
    this.onShowChange = this.onShowChange.bind(this);
    this.onShowSubChange = this.onShowSubChange.bind(this);
    this.onStepChange = this.onStepChange.bind(this);
    this.onColumnSortChange = this.onColumnSortChange.bind(this);
    if (props.initState) {
      this.state = props.initState;
    }
  }

  componentWillUnmount() {
    // Remember state for the next mount
    this.props.saveState(this.state);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.grade !== this.props.grade) {
      // If changing grades via navigation, reset the filter/page/sort state
      this.setState({
        offset: 0,
        limit: 50,
        sortOn: 'proposed',
        show: 'all',
        subShow: CURRENT,
        sortColumn: { columnName: 'full_name', order: ASCENDING }
      });
    }
  }

  refreshEmployees(limit = this.state.limit, offset = this.state.offset, sortColumn = this.state.sortColumn) {
    const { getEmployees, modelId, grade, basis } = this.props;
    const { sortOn, show, subShow } = this.state;
    offset = offset === this.state.offset ? 0 : offset;
    this.setState({ offset: offset, limit: limit, sortColumn: sortColumn }, () => {
      getEmployees(modelId, grade, limit, offset, sortColumn, sortOn, show, subShow, basis);
    });
  }

  getRowElement(cellData, idx) {
    const { preferences } = this.props;
    const { cells, original } = cellData;
    return (
      <React.Fragment>
        <AdvancedCell getCellProps={cells[0].getCellProps}>
          {original.full_name}
          <div className="id">{original.org_employee_id}</div>
        </AdvancedCell>
        <AdvancedCell getCellProps={cells[1].getCellProps} className="right-align">
          {original.org_job_title}
          <div className="proposed"> </div>
        </AdvancedCell>
        <AdvancedCell getCellProps={cells[2].getCellProps} className="right-align">
          {parseNumCheckNull(applyRounding(original.basis, 'monetary', preferences))}
          <div className="proposed"> </div>
        </AdvancedCell>
        <AdvancedCell getCellProps={cells[3].getCellProps} className="right-align">
          <span className="current">{this.getRoundedPercent(original.c_comp_ratio)}</span>
          <div className="proposed">{this.getRoundedPercent(original.p_comp_ratio)}</div>
        </AdvancedCell>
        <AdvancedCell getCellProps={cells[4].getCellProps} className="right-align">
          <span className="current">{this.getRoundedPercent(original.c_range_pen)}</span>
          <div className="proposed">{this.getRoundedPercent(original.p_range_pen)}</div>
        </AdvancedCell>
        <AdvancedCell getCellProps={cells[5].getCellProps} className="right-align">
          <span className="current">
            {parseNumCheckNull(applyRounding(original.c_min_cost, 'monetary', preferences))}
          </span>
          <div className="proposed">
            {parseNumCheckNull(applyRounding(original.p_min_cost, 'monetary', preferences))}
          </div>
        </AdvancedCell>
        <AdvancedCell getCellProps={cells[6].getCellProps} className="right-align">
          <span className="current">
            {parseNumCheckNull(applyRounding(original.c_above_max, 'monetary', preferences))}
          </span>
          <div className="proposed">
            {parseNumCheckNull(applyRounding(original.p_above_max, 'monetary', preferences))}
          </div>
        </AdvancedCell>
      </React.Fragment>
    );
  }

  getRoundedPercent(value) {
    const { preferences } = this.props;
    return value ? applyRounding(value * 100, 'percent', preferences) : '-';
  }

  onSortChange(sortOn) {
    this.setState({ sortOn: sortOn.toLowerCase() }, () => {
      this.refreshEmployees(this.state.limit, 0);
    });
  }

  onColumnSortChange(sortOn) {
    if (sortOn.length > 0)
      this.refreshEmployees(this.state.limit, 0, {
        columnName: sortOn[0].id,
        order: sortOn[0].desc ? DESCENDING : ASCENDING
      });
  }

  onStepChange(step) {
    this.refreshEmployees(parseInt(step), 0);
  }

  onShowChange(show) {
    // Handles logic to make sure default subShow selection is valid
    let subShowVal = null;
    if (show.value === 'below') {
      subShowVal = equalsZeroUndefined(this.props.gradeTotals.belowMin.current) ? PROPOSED : CURRENT;
    } else if (show.value === 'above') {
      subShowVal = equalsZeroUndefined(this.props.gradeTotals.aboveMax.current) ? PROPOSED : CURRENT;
    }
    this.setState({ show: show.value, subShow: subShowVal }, () => {
      this.refreshEmployees(this.state.limit, 0);
    });
  }

  onShowSubChange(subShow) {
    this.setState({ subShow: subShow.value }, () => {
      this.refreshEmployees(this.state.limit, 0);
    });
  }

  render() {
    const { gradeTotals, viewRef, employeeCount, employeeLoading, employeeData, basis, basisOptions } = this.props,
      { show, subShow, sortOn } = this.state;
    const onlyProposed = equalsZeroUndefined(gradeTotals.employeeCount.current);
    let filterOptions = [
      {
        value: 'all',
        label: 'All Employees'
      },
      {
        value: 'above',
        label: 'Employees above Max',
        isDisabled:
          equalsZeroUndefined(gradeTotals.aboveMax.current) && equalsZeroUndefined(gradeTotals.aboveMax.proposed)
      },
      {
        value: 'below',
        label: 'Employees below Min',
        isDisabled:
          equalsZeroUndefined(gradeTotals.belowMin.current) && equalsZeroUndefined(gradeTotals.belowMin.proposed)
      }
    ];
    let selectOptions = [
      {
        value: CURRENT,
        label: 'Current'
      },
      {
        value: PROPOSED,
        label: 'Proposed'
      }
    ];
    if (show === 'below') {
      selectOptions = [
        {
          value: CURRENT,
          label: 'Current' + (equalsZeroUndefined(gradeTotals.belowMin.current) ? ' (no employees)' : ''),
          isDisabled: equalsZeroUndefined(gradeTotals.belowMin.current)
        },
        {
          value: PROPOSED,
          label: 'Proposed' + (equalsZeroUndefined(gradeTotals.belowMin.proposed) ? ' (no employees)' : ''),
          isDisabled: equalsZeroUndefined(gradeTotals.belowMin.proposed)
        }
      ];
    } else if (show === 'above') {
      selectOptions = [
        {
          value: CURRENT,
          label: 'Current' + (equalsZeroUndefined(gradeTotals.aboveMax.current) ? ' (no employees)' : ''),
          isDisabled: equalsZeroUndefined(gradeTotals.aboveMax.current)
        },
        {
          value: PROPOSED,
          label: 'Proposed' + (equalsZeroUndefined(gradeTotals.aboveMax.proposed) ? ' (no employees)' : ''),
          isDisabled: equalsZeroUndefined(gradeTotals.aboveMax.proposed)
        }
      ];
    }

    columnHeaders.find(header => header.accessor === 'basis').Header = basisOptions.find(
      ({ value }) => value === basis
    ).label;
    // Needed to offset headers to allign correctly with body
    return (
      <div ref={this.scrollTarget} className="sma--table-employee-container">
        <div className="sma--table-filter-bar">
          <div className="sma--table-show-container">
            {'Show '}
            <Select
              className="sma--table-show-dropdown"
              options={filterOptions}
              isClearable={false}
              value={filterOptions.find(({ value }) => value === show)}
              onChange={this.onShowChange}
            />
            {show !== 'all' && !onlyProposed && (
              <React.Fragment>
                {'  In '}
                <Select
                  className="sma--table-show-dropdown"
                  options={selectOptions}
                  isClearable={false}
                  value={selectOptions.find(({ value }) => value === subShow)}
                  onChange={this.onShowSubChange}
                />
              </React.Fragment>
            )}
            {show !== 'all' && onlyProposed && '  in Proposed'}
          </div>
          <div className="sma--table-sorton-container">
            {'Sort on '}
            {onlyProposed ? (
              PROPOSED
            ) : (
              <Dropdown
                className="sma--table-sorton-dropdown"
                dropdownClass="sma--table-sorton-dropdown-select"
                itemOnClick={this.onSortChange}
                text={sortOn}
                dropdownItems={this.sortOnDropdownItems}
              />
            )}
          </div>
        </div>
        <AdvancedTable
          stickyHeader={true}
          columnHeaders={columnHeaders}
          rowData={employeeData}
          getData={this.refreshEmployees}
          headerRowClassName="sma--table__employee-header-row"
          rowClassName="sma--table__employee-row"
          getDataRow={this.getRowElement}
          onSortChange={this.onColumnSortChange}
          dataLoading={employeeLoading}
          noDataText="No Employees in This Grade"
          loadingText="Loading Employees..."
          allowSorting={true}
          useClientSideSort={false}
        />
        <div className="sma--table-employee__pagination">
          <Pagination
            step={this.state.limit}
            offset={this.state.offset}
            totalCount={employeeCount}
            pageAction={this.refreshEmployees}
            allowChangingLimit={true}
            onStepChange={this.onStepChange}
            scrollEl={viewRef}
            scrollTopPageChange={true}
            scrollTopOffset={-20}
            scrollTarget={this.scrollTarget}
          />
        </div>
      </div>
    );
  }
}
